// LLM generated - Model Claude 4 Sonnet

import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useDepositBonuses } from '~/domains/promotions/hooks/useDepositBonuses';
import { DepositBonusQueryKeys } from '~/domains/promotions/constants/query';
import type { NormalizedBonus } from '~/domains/promotions/types/bonus.types';

type UseDepositBonusSelectionProps = {
  selectedBonusId?: string;
  isDepositMatchBonusEnabled: boolean;
};

type UseDepositBonusSelectionReturn = {
  selectedBonus: NormalizedBonus | null;
  isLoadingBonuses: boolean;
};

/**
 * Hook for managing deposit bonus selection logic.
 * Handles fetching bonuses, query invalidation, and bonus selection.
 *
 * @param props - Configuration options
 * @param props.selectedBonusId - Optional specific bonus ID to select
 * @param props.isDepositMatchBonusEnabled - Feature flag state
 * @returns Selected bonus and loading state
 */
export function useDepositBonusSelection({
  selectedBonusId,
  isDepositMatchBonusEnabled,
}: UseDepositBonusSelectionProps): UseDepositBonusSelectionReturn {
  const queryClient = useQueryClient();

  // Fetch assigned bonuses when feature flag is enabled
  // Always fetch enough bonuses to ensure we can find a specific one if selected (until API supports filtering by bonusId)
  // Using a reasonable pageSize to cover most cases
  const {
    data: bonusesData,
    refetch: refetchBonuses,
    isLoading: isLoadingBonuses,
  } = useDepositBonuses({
    page: 1,
    pageSize: 20, // Fetch enough bonuses to find the selected one
    enabled: isDepositMatchBonusEnabled, // Only fetch when feature flag is enabled
  });

  // Invalidate and refetch bonuses when selectedBonusId changes, but only if bonus is not in current data
  useEffect(() => {
    if (selectedBonusId && isDepositMatchBonusEnabled) {
      // Only refetch if bonus not found in current data
      const bonusExists = bonusesData?.data?.some(
        (bonus) => bonus.id === selectedBonusId && bonus.isActive
      );
      if (!bonusExists) {
        // Invalidate the query cache to force a fresh fetch
        queryClient.invalidateQueries({ queryKey: DepositBonusQueryKeys.ALL_ASSIGNED() });
        refetchBonuses();
      }
    }
  }, [selectedBonusId, isDepositMatchBonusEnabled, refetchBonuses, queryClient, bonusesData]);

  // Select the appropriate bonus
  const selectedBonus = useMemo(() => {
    if (!bonusesData?.data || bonusesData.data.length === 0) {
      return null;
    }

    // If a specific bonus ID was provided (e.g., from clicking "Deposit" on a bonus),
    // find and use that bonus if it's in the assigned bonuses list
    if (selectedBonusId) {
      const foundBonus = bonusesData.data.find(
        (bonus) => bonus.id === selectedBonusId && bonus.isActive
      );
      if (foundBonus) {
        return foundBonus;
      }
      // If selected bonus not found or not active, fall back to first available
      // This can happen if the bonus is on a different page or no longer active
    }

    // Default: Return first active bonus
    return bonusesData.data.find((bonus) => bonus.isActive) || bonusesData.data[0];
  }, [bonusesData, selectedBonusId]);

  return {
    selectedBonus,
    isLoadingBonuses,
  };
}
